import { Info, ArrowUp, ArrowDown } from 'lucide-react';
import type { ReactElement } from 'react';

interface MetricCardProps {
  label: string;
  value: string | number | null;
  subtitle?: string | null; // denominator / context line, e.g. "of 234 votes"
  delta?: string | null; // e.g. "+2.5%" or "-1.2%"
  isPrimary?: boolean;
  /** Optional explanatory tooltip — shows a small ⓘ next to the label. */
  tooltip?: string;
  /** Compact padding variant for KPI strips with many cards in a row. */
  compact?: boolean;
  /** Optional small visual (e.g. a proportion bar) rendered under the value. */
  bar?: ReactElement | null;
}

export function MetricCard({ label, value, subtitle, delta, isPrimary = false, tooltip, compact = false, bar }: MetricCardProps) {
  const isPositive = delta && delta.startsWith('+');
  const isNegative = delta && delta.startsWith('-');
  const padding   = compact ? 'p-[12px_14px]' : 'p-[20px_24px]';
  const valueSize = compact ? 'text-[24px]'   : 'text-[32px]';

  return (
    <div
      className={`
        relative bg-[var(--surface)] border-[1px] border-[var(--border)] rounded-[var(--r2)] ${padding}
        shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow duration-150
        flex flex-col justify-center gap-[var(--s2)] h-full
        ${isPrimary ? 'border-l-[3px] border-l-[var(--accent)]' : ''}
        ${tooltip ? 'group/metric' : ''}
      `}
    >
      <div className="font-[family-name:var(--font-mono)] text-[11px] uppercase text-[var(--text-3)] leading-none flex items-center gap-[5px]">
        <span>{label}</span>
        {tooltip && (
          <Info
            size={11}
            className="text-[var(--text-3)]/60 group-hover/metric:text-[var(--text-2)] transition-colors cursor-help shrink-0"
            aria-label={tooltip}
          />
        )}
      </div>
      {tooltip && (
        // Fixed-width tooltip; min/max-w keeps it readable inside narrow KPI cards
        // and prevents it from being clipped by the card's own boundaries.
        <div className="pointer-events-none absolute left-0 top-[calc(100%+6px)] z-30 w-[240px] max-w-[calc(100vw-32px)] bg-[var(--surface)] border border-[var(--border)] text-[var(--text-2)] text-[11px] leading-snug p-[10px_12px] rounded-[var(--r2)] shadow-md opacity-0 group-hover/metric:opacity-100 transition-opacity font-[family-name:var(--font-display)] normal-case font-normal whitespace-normal">
          {tooltip}
        </div>
      )}
      {value === null ? (
        <div className="animate-pulse flex flex-col gap-[var(--s2)]">
          <div className="h-8 w-24 bg-[var(--border)] rounded" />
          <div className="h-3 w-32 bg-[var(--border)] rounded" />
        </div>
      ) : (
        <>
          <div className={`font-[family-name:var(--font-display)] font-light ${valueSize} text-[var(--text-1)] leading-none`}>
            {value}
          </div>
          {subtitle && (
            <div className="font-[family-name:var(--font-mono)] text-[11px] text-[var(--text-3)] leading-none">
              {subtitle}
            </div>
          )}
          {delta && (
            <div
              className={`font-[family-name:var(--font-mono)] text-[11px] leading-none flex items-center gap-[4px] mt-[4px]
                ${isPositive ? 'text-[var(--success)]' : (isNegative ? 'text-[var(--accent)]' : 'text-[var(--text-2)]')}
              `}
            >
              {isPositive && <ArrowUp size={11} className="shrink-0" />}
              {isNegative && <ArrowDown size={11} className="shrink-0" />}
              {delta}
            </div>
          )}
          {bar && <div className="mt-[6px]">{bar}</div>}
        </>
      )}
    </div>
  );
}
